/** @jsxImportSource @emotion/react */
import { useContext, useEffect, useMemo, useState } from 'react';

import useTranslate from '@common/hooks/useTranslate';
import { formatPrice, sendGAEvent } from '@common/utlis';
import Button from '@stories/atoms/Button/Button';
import FilterPillRemove from '@stories/atoms/FilterPill/FilterPillRemove';
import Select from '@stories/atoms/FormElements/Select/Select';
import { media, mq } from '@stories/theming/settings';
import { FORMAT_CURRENCY_OPTIONS } from '@utils/constants';
import { getNumberOfActiveFilters, isSortingOrder, useProductListContext } from '@utils/productList';

import { PageContext } from '../../Context/pageContext';

import type { CssProps } from '@stories/@types/custom';

type TopBarProps = CssProps;

const translations = {
  'n stars and more': { de_DE: '${min} Sterne und mehr' },
  price_asc: { de_DE: 'Sortieren: Niedrigster Preis' },
  price_desc: { de_DE: 'Sortieren: Höchster Preis' },
  relevance_desc: { de_DE: 'Sortieren: Relevanz' },
  discount_desc: { de_DE: 'Sortieren: Angebote' },
  new_arrivals_desc: { de_DE: 'Sortieren: Neuheiten' },
  score_top_seller_desc: { de_DE: 'Sortieren: Bestseller' },
  user_rating_desc: { de_DE: 'Sortieren: Kundenbewertung' },
  'Price: ${min} - ${max}': { de_DE: 'Preis: ${min} - ${max}' },
  'Price: ${min}+': { de_DE: 'Preis: ab ${min}' },
  'filters${activeFilterCount}': { de_DE: 'Filter${activeFilterCount}' },
  'clear all': { de_DE: 'Alle Filter zurücksetzen' }
};

const ActiveFilterPills: React.FC = () => {
  const translate = useTranslate(translations);
  const {
    activeFiltersAndSorting: { activeFilters, currentSortingOrder },
    defaultSettings: { defaultSortingOrder },
    reducerFunctions: { uncheckFilter, clearFilters, setCurrentSortingOrder }
  } = useProductListContext();

  const { storeCode } = useContext(PageContext);
  const formatCurrencyOptions = useMemo(
    () => ({
      ...(FORMAT_CURRENCY_OPTIONS[storeCode] || FORMAT_CURRENCY_OPTIONS['vomachterhof']),
      maximumFractionDigits: 0
    }),
    [storeCode]
  );

  type TLabelTransformation = (label: string) => [string, { [key: string]: string | number }];
  const labelTransformations: { [key: string]: TLabelTransformation } = useMemo(
    () => ({
      averageRating: (label) => {
        const [min] = label.split('-');
        return ['n stars and more', { min: Number(min) }];
      },
      sorting_mobile: (label) => [label, {}],
      price_range: (label) => {
        const [min, max] = label.split('-');
        if (typeof max === 'undefined') {
          return ['Price: ${min}+', { min: formatPrice(min, formatCurrencyOptions) }];
        }
        return [
          'Price: ${min} - ${max}',
          { min: formatPrice(min, formatCurrencyOptions), max: formatPrice(max, formatCurrencyOptions) }
        ];
      }
    }),
    [formatCurrencyOptions]
  );

  if (
    getNumberOfActiveFilters(activeFilters) === 0 &&
    (currentSortingOrder === undefined || currentSortingOrder === defaultSortingOrder)
  ) {
    return null;
  }

  return (
    <div
      css={mq({
        borderBottom: ['none', null, 'solid thin var(--color-border-grey)'],
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        padding: ['.5rem 0 0', null, '.75rem 0']
      })}
    >
      {Object.keys(activeFilters).flatMap((key) =>
        activeFilters[key].map((filterName) => {
          const labelTransformation = labelTransformations[key];
          const label = labelTransformation ? translate(...labelTransformation(filterName)) : filterName;
          return (
            <FilterPillRemove
              label={label}
              onClick={() => uncheckFilter({ section: key, option: filterName })}
              key={filterName}
            />
          );
        })
      )}
      {currentSortingOrder && currentSortingOrder !== defaultSortingOrder && (
        <FilterPillRemove
          label={translate(currentSortingOrder)}
          onClick={() => setCurrentSortingOrder(defaultSortingOrder)}
        />
      )}
      {getNumberOfActiveFilters(activeFilters) > 1 && (
        <button
          css={(t) => ({
            ...t.font.small,
            color: t.color.highlight,
            margin: '0 1rem',
            ':hover': { color: t.color.hover.highlight }
          })}
          onClick={() => clearFilters()}
        >
          {translate('clear all')}
        </button>
      )}
    </div>
  );
};

const TopBar: React.FC<TopBarProps> = ({ cssProps }) => {
  const translate = useTranslate(translations);
  const { setFilterOverlayShown, showOverlay } = useContext(PageContext);
  // const { currentCategoryId } = useProductListContext();
  const {
    activeFiltersAndSorting: { activeFilters, currentSortingOrder, currentPage },
    productsAndAvailableFilters: { total },
    reducerFunctions: { setCurrentSortingOrder },
    defaultSettings: { productsPerPage, sortingOptions, defaultSortingOrder }
  } = useProductListContext();
  // const currentCategory = findCategory(currentCategoryId, categoryTree);
  const from = productsPerPage * (currentPage - 1) + 1;
  const to = Math.min(productsPerPage * currentPage, total);

  const [searchTermFromUrl, setSearchTermFromUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');
    setSearchTermFromUrl(searchQuery || undefined);
  }, []);

  return (
    <div css={mq({ marginBottom: ['.5rem', null, '1rem'], ...(cssProps as object) })}>
      <div
        css={mq({
          borderBottom: 'thin solid var(--color-border-grey)',
          paddingBottom: ['.5rem', null, '.75rem']
        })}
      >
        {/* Disable for category A/B test */}
        {/* <div
          id="scrollableSubcategories"
          css={{
            overflow: 'scroll',
            display: 'flex',
            ...noScrollbarStyle,
            margin: '0 -1rem 1.5rem',
            [media('desktop')]: { display: 'none' }
          }}
        >
          <div
            css={{
              display: 'flex',
              gap: '0.5rem',
              padding: '0 1rem'
            }}
          >
            {currentCategory?.children?.map(({ url, type, id, name }) => (
              <LinkSwitched
                href={makeUrlAbsolute(url)}
                pathname={`/[store]/${type}/[categoryId]`}
                query={{ store: storeCode, categoryId: id }}
                key={id}
                scroll={false}
                cssProps={(t) => ({
                  border: `1px solid ${t.color.grey.g92}`,
                  borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  whiteSpace: 'nowrap'
                })}
              >
                {name}
              </LinkSwitched>
            ))}
          </div>
        </div> */}

        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            [media('desktop')]: {
              alignItems: 'flex-end'
            }
          }}
        >
          <div
            css={{
              display: 'block',
              flex: 'auto 1 0',
              fontSize: '0.85em',
              lineHeight: '100%'
            }}
          >
            {from}-{to} von <span css={{ fontWeight: 'bold' }}> {total} </span>
            <span css={{ display: 'inline' }}>Produkte</span>
            {searchTermFromUrl && (
              <>
                <span css={{ fontWeight: 'bold' }}> für</span> {searchTermFromUrl}
              </>
            )}
          </div>
          <Select
            cssProps={{
              display: 'none',
              flex: 'auto 0 0',
              [media('desktop')]: {
                display: 'block'
              }
            }}
            options={sortingOptions.map((option) => ({
              ...option,
              checked: option.value === currentSortingOrder
            }))}
            onChange={(e) =>
              setCurrentSortingOrder(isSortingOrder(e.target.value) ? e.target.value : defaultSortingOrder)
            }
            description="Sortieren"
          />
          <Button
            cssProps={{ [media('desktop')]: { display: 'none' } }}
            label={translate('filters${activeFilterCount}', {
              activeFilterCount:
                getNumberOfActiveFilters(activeFilters) > 0
                  ? ` (${getNumberOfActiveFilters(activeFilters)})`
                  : ''
            })}
            outline
            size="small"
            attrs={{
              onClick: () => {
                setFilterOverlayShown(true);
                sendGAEvent({ category: 'Search Filter', action: 'Mobile Filter', label: 'open' });
                showOverlay({ callback: () => setFilterOverlayShown(false) });
              }
            }}
          />
        </div>
      </div>
      <ActiveFilterPills />
    </div>
  );
};

export default TopBar;
